/** What the write-error screen shows, and how its Retry button recovers. */
export interface MessagesWriteErrorFields {
  /** Short heading, e.g. "Couldn't save your messages". */
  title: string;
  /** The screen's explanation of the failure. */
  detail: string;
  /** Re-sends the failed write. */
  retry: () => Promise<unknown>;
}

/**
 * Thrown when saving a thread's messages fails after all retries.
 * `MessageWriteErrorBoundary` catches it and shows a crash screen with a Retry
 * button (ADR 065). The code that throws it must:
 *
 * - Throw during render. An error boundary does not catch errors thrown from
 *   async callbacks.
 * - Make `retry` call the request function directly. The boundary unmounts the
 *   component that threw, along with its mutation hooks.
 * - Leave the cache as it should be after the write, because a successful
 *   Retry re-mounts the app, which reads the cache. (`NewChat`'s retry loads
 *   the new chat's page instead.)
 *
 * See `useCommitMessages` and `NewChat`.
 */
export class MessagesWriteError extends Error {
  readonly fields: MessagesWriteErrorFields;

  constructor(fields: MessagesWriteErrorFields) {
    super(fields.title);
    this.name = "MessagesWriteError";
    this.fields = fields;
  }
}
